import { Injectable } from '@angular/core';
import { AdminService } from '@mitel/cloudlink-sdk/admin';
import { ChatService } from '@mitel/cloudlink-sdk/chat';
import { NotificationsService } from '@mitel/cloudlink-sdk/notifications';
import { Config } from '@mitel/cloudlink-sdk';
import { Subject, ReplaySubject } from 'rxjs';
import { Conversation } from '../Conversation';
import { filter, map } from 'rxjs/operators';

export interface UserSearchResult {
  userId: string;
  displayName: string;
}

@Injectable({
  providedIn: 'root'
})
export class ConversationListService {
  public chatService: ChatService;
  private adminService: AdminService;

  loggedInAccountId: string;
  loggedInUserId: string;

  notify: NotificationsService;

  public conversationsChanged$: Subject<Conversation> = new Subject<Conversation>();
  public conversationsDeleted$: Subject<string> = new Subject<string>();

  public ready$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor() {}

  async getConversations(extraParams?, loadMore?): Promise<(Conversation[] | string)[]> {
    this.createChatService();

    let params = { userId: this.loggedInUserId };
    params['expand'] = 'conversation';

    let mergedParams = extraParams ? { ...extraParams, ...params } : params;

    let res = await this.chatService.getUserConversations(mergedParams);
    let result = res.body;

    let embedded = result['_embedded'];
    let next = result['_links'].next;

    let skipToken = null;
    if (next) {
      let stringToFind = '&$skipToken=';
      let skipTokenToEnd = next.substring(next.lastIndexOf(stringToFind) + stringToFind.length);
      skipToken = skipTokenToEnd.substring(0, skipTokenToEnd.indexOf('&$'));
    }

    if (typeof embedded === 'undefined') {
      return [];
    }

    let resultConversationList = result['_embedded']['items'];

    let conversationList = [];
    for (var i = 0; i < resultConversationList.length; i++) {
      let conversation = new Conversation();
      conversation.id = resultConversationList[i]['conversation']['conversationId'];
      conversation.stream = resultConversationList[i]['conversation']['stream'];
      conversation.displayName = conversation.stream
        ? resultConversationList[i]['conversation']['name']
        : this.getParticipantsString(resultConversationList[i]['conversation']);

      conversationList.push(conversation);
    }

    return [conversationList, skipToken];
  }

  getParticipantsString(conversation) {
    let participantList = conversation['_embedded']['participants']['_embedded']['items'];
    let userNameList = [];

    for (var i = 0; i < participantList.length; i++) {
      if (participantList[i].userId !== this.loggedInUserId) {
        try {
          if (participantList[i]['_embedded']) {
            let userName = participantList[i]['_embedded']['users']['name'];
            //name can be null
            if (userName) {
              userNameList.push(userName);
            } else {
              userNameList.push(participantList[i].userId);
            }
          } else {
            userNameList.push(participantList[i].userId);
          }
        } catch (ex) {
          console.log('getParticipantsString()', ex);
          userNameList.push('Error');
        }
      }
    }

    return this.joinNames(userNameList);
  }

  private joinNames(names: string[]): string {
    let participantString = '';
    for (var i = 0; i < names.length; i++) {
      if (i > 0) {
        participantString += i == names.length - 1 ? ', and ' : ', ';
      }

      participantString += names[i];
    }

    return participantString;
  }

  // Unlike the REST fetch (getConversations(), which passes expand=conversation), the
  // websocket "conversations" push notification only carries participant userIds - no
  // expanded user/name info - so getParticipantsString() falls back to raw IDs here. Resolve
  // the real names via the admin directory instead.
  private async resolveDirectDisplayName(conversation): Promise<string> {
    const participantList = conversation?.['_embedded']?.['participants']?.['_embedded']?.['items'] ?? [];
    const otherParticipantIds: string[] = participantList
      .map(participant => participant.userId)
      .filter(userId => userId && userId !== this.loggedInUserId);

    if (!otherParticipantIds.length) {
      return '';
    }

    const nameByUserId = await this.resolveUserNames(otherParticipantIds);
    return this.joinNames(otherParticipantIds.map(userId => nameByUserId.get(userId) || userId));
  }

  private async resolveUserNames(userIds: string[]): Promise<Map<string, string>> {
    this.createAdminService();

    const res = await this.adminService.getUsersByAccountId({
      accountId: this.loggedInAccountId,
      items: userIds.join(','),
      itemKeys: 'userId'
    });

    const users = res.body?._embedded?.items ?? [];
    return new Map(
      users.filter(user => user.userId).map(user => [user.userId!, user.displayName || user.name || user.userId!])
    );
  }

  public setUpSubscriptions() {
    const expiryDateTime = new Date();
    expiryDateTime.setHours(expiryDateTime.getHours() + 12);

    this.createChatService();

    this.chatService
      .notifications()
      .beginSubscribe()
      .appendResource({ type: 'conversations' })
      .endSubscribe({ transport: 'websocket', expiryDateTime })
      .then(subscription => {
        const deleted$ = this.chatService
          .notifications()
          .asObservable({
            subscription: subscription,
            resource: 'conversations'
          })
          .pipe(
            filter(publication => publication.method === 'DELETE'),
            map(publication => publication.subject.split('/')?.pop())
          );

        deleted$.subscribe(conversation => {
          if (conversation) {
            this.conversationsDeleted$.next(conversation);
            console.log('CONVERSATION DELETED');
          }
        });

        const changed$ = this.chatService
          .notifications()
          .asObservable({
            subscription: subscription,
            resource: 'conversations'
          })
          .pipe(
            filter(publication => publication.method !== 'DELETE'),
            map(publication => publication.content)
          );

        changed$.subscribe(async returnedConversation => {
          if (returnedConversation) {
            let convo = new Conversation();
            convo.stream = returnedConversation.stream;
            convo.id = returnedConversation.conversationId;
            convo.displayName = returnedConversation.stream
              ? returnedConversation['name']
              : await this.resolveDirectDisplayName(returnedConversation);
            this.conversationsChanged$.next(convo);
            console.log('CONVERSATIONS CHANGED');
          }
        });

        this.ready$.next(true);
      })
      .catch(error => {
        console.log('FAILED TO SUBSCRIBE');
        console.log(error);
      });
  }

  createChatService() {
    if (!this.chatService && this.notify) {
      this.chatService = new ChatService({ config: Config.global });
    }
  }

  private createAdminService() {
    if (!this.adminService) {
      this.adminService = new AdminService({ config: Config.global });
    }
  }

  // Mirrors mitel-messaging's UsersService directory search (AdminService.getUsersByAccountId
  // with a $search term) - used to look up the other participant when starting a new chat.
  public async searchUsers(search: string): Promise<UserSearchResult[]> {
    this.createAdminService();

    const res = await this.adminService.getUsersByAccountId({
      accountId: this.loggedInAccountId,
      search,
      top: 10
    });

    const users = res.body?._embedded?.items ?? [];
    return users
      .filter(user => user.userId && user.userId !== this.loggedInUserId)
      .map(user => ({
        userId: user.userId!,
        displayName: user.displayName || user.name || user.userId!
      }));
  }

  // Mirrors mitel-messaging's createChatWithUsers()/#openChatWithCloudlinkId(): reuse an
  // existing direct conversation with these participants if one exists, otherwise create one.
  // Direct conversations aren't limited to 2 participants - the SDK only treats a conversation
  // as a stream (named group) when `stream`/`name` are explicitly set, which we never do here.
  public async createChatWithUserIds(otherUserIds: string[]): Promise<string> {
    const existingConversationId = await this.getConversationsWithUserIds([...otherUserIds, this.loggedInUserId]);
    if (existingConversationId) {
      return existingConversationId;
    }

    return await this.createConversation(otherUserIds);
  }

  public async getConversationsWithUserIds(userIds: string[]): Promise<string> {
    let participants = 'contains-exact(' + userIds.join(',') + ')';
    let res = await this.chatService.getConversations({
      participants
    });

    let result = res.body;

    let conversationId = '';
    if (result?.count > 0 && result?._embedded?.items.length > 0) {
      conversationId = result._embedded.items[0].conversationId;
    }

    if (conversationId) {
      console.log('Conversation with ID exists: ' + conversationId);
    } else {
      console.log('No Conversation for participants: ' + participants);
    }
    return conversationId;
  }

  // Pass streamName to create a named/group stream instead of a direct conversation - same
  // SDK call either way, mirroring mitel-messaging's createConversation(userIds, streamName?).
  // The API rejects `participants` for stream conversations - `originalParticipants` is
  // required there instead (deprecated-but-fine `participants` still works for direct).
  public async createConversation(otherParticipantUserIds: string[], streamName?: string) {
    const res = await this.chatService.createConversation({
      body: streamName
        ? {
            originalParticipants: otherParticipantUserIds.map(participantId => ({ type: 'USER', participantId })),
            stream: true,
            name: streamName
          }
        : { participants: otherParticipantUserIds, stream: false }
    });

    const conversationResponse = res.body;
    const conversationId = conversationResponse['conversationId'];
    console.log('Conversation created with ID: ' + conversationId, conversationResponse);
    return conversationId;
  }

  public async deleteConversation(conversationId: string) {
    console.log('Deleting conversation with ID: ' + conversationId);
    return await this.chatService.deleteConversation({
      conversationId: conversationId
    });
  }
}
